import { computed, DestroyRef, effect, inject, Injectable, InjectionToken, isDevMode, type Signal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { filter, map } from 'rxjs';
import { environment } from '@environment';
import { IDENTITY_DATA, IdentityStore } from '@lite-workspace/shared/identity';
import {
  clipLessonId,
  type ActiveHint,
  type LibraryRequest,
  type OnboardingRecord,
  type PlaylistGroup,
  type PlaylistLesson,
} from './catalog.types';
import { ONBOARDING_CATALOG } from './onboarding-catalog.token';
import { OnboardingMediaService } from './ui/onboarding-media.service';

/** Cabinet routes of the student side: the photographer onboarding never speaks there. */
const CLIENT_URL = /^\/client(\/|$|\?)/;

/** Dev-only re-walk of the whole flow on one account, instead of a new account per walk. */
const REPLAY_PARAM = /[?&]onboardingReplay=1(?:&|$)/;

/** Сколько приветствие ждёт после входа в кабинет, прежде чем показаться новичку. */
export const NEWCOMER_DELAY_MS = 7000;

/** Окно новичка поднимается раз на вкладку: метка живёт ровно столько же. */
const NEWCOMER_KEY = 'onboarding.library.newcomerShown';

/** Две съёмки — фотограф уже работает, и приветствие само больше не встаёт. */
const NEWCOMER_SHOOTS = 2;

/** Урок, с которого начинается библиотека у новичка; ролика к нему нет — берём первый с роликом. */
const NEWCOMER_LESSON = 'upload-modes';

/** Карточка у знака вопроса: встаёт после первого закрытия окна, поднятого новичку. */
export const LIBRARY_DOOR_HINT = 'library-door';

/** Приветствие с видеоуроками: пошёл смотреть — отметка «пройдено», больше оно не встаёт. */
export const WELCOME_FLOW = 'welcome-video';

/**
 * «Позже» — отметка скрытия с датой в имени: сегодня приветствие молчит на любом устройстве, завтра
 * встаёт снова. Дата календарная, по часам фотографа.
 */
// ponytail: одна отметка на каждый день с «Позже» копится в профиле до «Смотреть видео» или второй
// съёмки; чистка — когда отметок станет заметно много.
export function welcomeLaterFlow(now: Date): string {
  const day = [now.getFullYear(), now.getMonth() + 1, now.getDate()].map(part => String(part).padStart(2, '0'));

  return `${WELCOME_FLOW}-later-${day.join('-')}`;
}

/** Мастер создания проекта: он живёт внутри оболочки кабинета, но окно новичку поверх него не всплывает. */
const WIZARD_SCREEN = 'project-wizard';

/** Прогон с нуля вне прода: ключ держит закрытое этой вкладкой вместо флагов аккаунта. */
const FRESH_KEY = 'onboarding.fresh';

/** Ключ читается один раз за подъём движка; нет ключа, прод или нет хранилища — `null`, и правит сервер. */
export function onboardingFreshIds(): ReadonlySet<string> | null {
  if (!environment.allowOnboardingReplay) return null;

  try {
    const raw = localStorage.getItem(FRESH_KEY);
    const ids: unknown = raw === null ? null : JSON.parse(raw);

    return Array.isArray(ids) ? new Set<string>(ids) : null;
  } catch {
    return null;
  }
}

/** Показывали ли окно новичку в этой вкладке. Хранилище недоступно (приватный режим) — считаем, что нет. */
function newcomerShown(): boolean {
  try {
    return !!sessionStorage.getItem(NEWCOMER_KEY);
  } catch {
    return false;
  }
}

function markNewcomerShown(): void {
  try {
    sessionStorage.setItem(NEWCOMER_KEY, '1');
  } catch {
    // Хранилища нет — метки тоже: окно всплывёт ещё раз в следующей вкладке.
  }
}

/**
 * A site the card may stand on: connected and rendering a box. A detached route or a `display: none`
 * host (shell nav below `lg`) gives a zero rect — the card would land in the top-left corner.
 */
function hasBox(site: HTMLElement | undefined): site is HTMLElement {
  return !!site?.isConnected && site.getClientRects().length > 0;
}

const BREAKPOINT_LG = 1024;

/** The shell shows the project tabs from `lg` up; below it their anchors render no box. */
const TABS_LAYOUT_QUERY = `(min-width: ${BREAKPOINT_LG}px)`;

/**
 * The host's own half of the audience rule — «this session is a photographer», which only the app
 * knows (the organizer chrome lives in its role service). Read inside a computed, so a host reading
 * its own signals there keeps the verdict reactive. Unprovided → the host adds no restriction.
 */
export const ONBOARDING_HOST_AUDIENCE = new InjectionToken<() => boolean>('ONBOARDING_HOST_AUDIENCE', {
  providedIn: 'root',
  factory: () => () => true,
});

/**
 * The onboarding engine: it knows every hint and lesson of the catalog, which screen the
 * photographer stands on and what he already finished or closed. It draws nothing — the coach card
 * and the lesson library read these signals.
 */
@Injectable({ providedIn: 'root' })
export class OnboardingService {
  private readonly catalog = inject(ONBOARDING_CATALOG);
  /** Хост без порта личности (студенческий редактор галереи) поднимает те же якоря — и молчит. */
  private readonly identity = inject(IDENTITY_DATA, { optional: true }) ? inject(IdentityStore) : null;
  private readonly router = inject(Router);
  private readonly hostAudience = inject(ONBOARDING_HOST_AUDIENCE);
  private readonly media = inject(OnboardingMediaService);

  /**
   * Переобход подсказок в деве: параметр достаточно увидеть один раз — до конца сессии вкладки
   * пройденное и скрытое игнорируются, даже когда навигация уносит параметр из адреса. Ничего
   * не пишем: прогон не должен трогать настоящие флаги аккаунта. Метка ставится на самом потоке
   * адресов, а не в computed: тот ленив и параметр мог бы уехать раньше первого чтения.
   */
  private readonly replay = signal(false);
  /** Закрытое в прогоне: сервер не трогаем, но крестик и «Дальше» скрывают карточку до конца вкладки. */
  private readonly replayClosed = signal<ReadonlySet<string>>(new Set());
  /** Просмотренное в прогоне: пометка живёт до конца вкладки, в профиль ничего не уходит. */
  private readonly replayViewed = signal<ReadonlySet<string>>(new Set());
  /** Избранное в прогоне: как и просмотренное — своё, до конца вкладки. `null` — сердечка ещё не было. */
  private readonly replayFavorites = signal<ReadonlySet<string> | null>(null);

  /**
   * Адрес меняется с началом перехода, а не с его концом: соседний экран грузится своим чанком, и
   * до конца навигации фотограф уже смотрит на «Загружаем…». Ждал бы движок `NavigationEnd` —
   * карточка покинутого экрана висела бы над загрузкой. Отменённый или упавший переход возвращает
   * адрес туда, где фотограф и остался.
   */
  private readonly url = toSignal(
    this.router.events.pipe(
      filter(
        (e): e is NavigationStart | NavigationEnd | NavigationCancel | NavigationError =>
          e instanceof NavigationStart ||
          e instanceof NavigationEnd ||
          e instanceof NavigationCancel ||
          e instanceof NavigationError,
      ),
      map(e => {
        const url = this.noteScreen(this.noteReplay(this.eventUrl(e)));
        if (e instanceof NavigationEnd) this.noteArrival(url);

        return url;
      }),
    ),
    { initialValue: this.noteReplay(this.router.url) },
  );

  /**
   * Anchor id → the elements hosts render for it, newest first; a hint without one never shows. Two
   * hosts may hold one id at once: a detached editor keeps its tab while the shell nav registers its own.
   */
  private readonly anchors = signal<ReadonlyMap<string, readonly HTMLElement[]>>(new Map());
  /**
   * Anchor id → the entry of the closed container (menu, popover) holding that anchor. Карточка
   * стоит на этом входе — и пока меню закрыто, и когда фотограф открыл его сам (R44).
   */
  private readonly reveals = signal<ReadonlyMap<string, HTMLElement>>(new Map());
  /**
   * Anchor id → the «?» a host renders beside that place. Значок помощи перевешивает и вход, и сам
   * якорь: почти у каждого места он есть, и карточка стоит именно на нём — носик упирается в значок,
   * а не в пустоту рядом с ним (R46). Нажатие на «?» карточку не проходит: его ведёт `requestHint`.
   * Every «?» of an id, newest claim first: a repeated row keeps one «?» per row.
   */
  private readonly helps = signal<ReadonlyMap<string, readonly HTMLElement[]>>(new Map());
  /** Click teardown of an `anchor-click` record, by anchor id and element. */
  private readonly anchorClicks = new Map<string, Map<HTMLElement, () => void>>();

  private readonly libraryRequestState = signal<LibraryRequest | null>(null);
  private librarySeq = 0;
  /** Урок, на который ведёт открытое приветствие новичка; `null` — приветствия на экране нет. */
  private readonly welcomeState = signal<string | null>(null);
  /** Когда библиотеку закрыли в последний раз — метка для тех, кто выжидает свою паузу после неё. */
  private readonly libraryClosedAtState = signal<number | null>(null);

  /**
   * Запись, которую фотограф попросил сам — нажатием на «?» рядом с местом. Такая карточка встаёт
   * впереди очереди: и закрытая когда-то, и не дошедшая до показа. Отпускается первым же
   * закрытием или перелистыванием — дальше очередь экрана живёт как обычно.
   */
  private readonly requestedState = signal<string | null>(null);
  /** Экран, где нажали «?»: начатый переход прячет карточку сразу, а снимает её только состоявшийся. */
  private readonly requestedOnScreen = signal<string | null>(null);
  /** Просьбу подал сам фотограф нажатием «?», а не хост (`offerHint`). */
  private readonly requestedByPress = signal(false);

  readonly currentScreen = computed<string | null>(() => this.screenOf(this.url()));

  /**
   * Все экраны каталога, чей адрес совпал: `currentScreen` — первый из них. Запись встаёт в очередь
   * везде, где совпал адрес её экрана, — запись широкого экрана (весь проект) держит место в очереди
   * каждого узкого под ним, и её закрытие одно на все.
   */
  private readonly screensHere = computed<ReadonlySet<string>>(() => {
    const url = this.url();

    return new Set(this.catalog.screens.filter(screen => new RegExp(screen.urlPattern).test(url)).map(screen => screen.id));
  });

  /**
   * Кому вообще говорит онбординг — и карточка коуча, и **?** с библиотекой. Нет профиля — нет и
   * вердикта: до его загрузки подсказка молчит, иначе карточка мигнула бы на первом кадре и уехала,
   * когда придут уже пройденные флаги.
   */
  readonly audience = computed(() => !!this.identity?.profile() && this.hostAudience() && !CLIENT_URL.test(this.url()));

  /**
   * Листание карточек текущего экрана: на какой из них стоим. Экран в метке — рамка листания:
   * ушли с экрана, и обход начинается заново, с первой незакрытой.
   */
  private readonly paging = signal<{ screen: string | null; index: number }>({ screen: null, index: 0 });

  /**
   * Повтор подсказок экрана, запрошенный из меню «?»: обход идёт по всей очереди, закрытые карточки
   * включая. Метка живёт до ухода с экрана и говорит движку две вещи — показывать сразу, без
   * ожидания сборки экрана, и не поднимать следующую незакрытую карточку после крестика.
   */
  private readonly replayingState = signal(false);

  /**
   * Экран, на котором фотограф закрыл карточку крестиком: остальные карточки его очереди
   * до перезагрузки вкладки молчат — и те, чей якорь появится позже. Живёт только в памяти: скрывать
   * их на сервере значит писать профиль пачкой. Снимает метку повтор из меню «?».
   */
  private readonly silencedScreen = signal<string | null>(null);

  /** Экран, под которым заведено нынешнее листание: сменился — обход начинается заново. */
  private pagedScreen = this.screenOf(this.router.url);
  /** Экран последнего состоявшегося перехода: отменённый guard'ом переход его не меняет. */
  private arrivedScreen = this.screenOf(this.router.url);

  /** Прогон с нуля: ключ заменяет флаги аккаунта, пока сброс на сервере недоступен. */
  private readonly fresh = signal(onboardingFreshIds());

  /** Пройденное и скрытое: в прогоне — своё, до конца вкладки, на сервер ничего не уходит. */
  private readonly closed = computed<ReadonlySet<string>>(() => {
    if (this.replay()) return this.replayClosed();

    const fresh = this.fresh();
    if (fresh) return fresh;

    const progress = this.identity?.profile()?.onboardingProgress;

    return new Set([...(progress?.completed ?? []), ...(progress?.dismissed ?? [])]);
  });

  /** Crossing `lg` changes which anchors render a box; the box reads themselves are not reactive. */
  private readonly tabsLayout: Signal<boolean> = this.watchTabsLayout();

  /**
   * Карточки коуча этого экрана в порядке каталога — и открытые, и уже закрытые: очередь у экрана
   * одна на все сессии, закрытая карточка держит в ней своё место для счётчика и «назад».
   */
  private readonly queue = computed<ActiveHint[]>(() => this.buildQueue());

  /** То же, что очередь, но одними записями — для футера карточки и его счётчика. */
  readonly screenQueue = computed<OnboardingRecord[]>(() => this.queue().map(hint => hint.record));

  /** Сколько карточек у этого экрана — счётчик пункта «Подсказки на экране»; нет ни одной — пункт мёртв. */
  readonly screenHintCount = computed<number>(() => this.queue().length);

  /** Идёт ли повтор подсказок экрана: показ не ждёт сборки экрана, пока метка стоит. */
  readonly replaying = this.replayingState.asReadonly();

  /**
   * Место в очереди. Листаем — стоим там, куда встали, прижимаясь к последней карточке, если
   * очередь укоротилась. Не листаем — на первой незакрытой; все закрыты, показывать нечего: `-1`.
   */
  private readonly index = computed<number>(() => {
    const queue = this.queue();
    // Нажали «?» — фотограф спросил об одном месте, и ответ ему одна карточка: счётчика и стрелок у неё
    // нет, даже когда запись стоит и в очереди экрана. Листать рядом со спрошенной нечего.
    if (this.requested()) return -1;

    // Крестик на этом экране уже был: очередь молчит, спрошенная через «?» карточка — выше — нет.
    if (this.silencedScreen() !== null && this.silencedScreen() === this.currentScreen()) return -1;

    if (this.paging().screen === this.currentScreen()) return Math.min(this.paging().index, queue.length - 1);

    // Крестик посреди повтора закрывает весь повтор, а не одну карточку: очередь экрана уже прожита,
    // и поднимать оставшуюся незакрытую запись — возвращать фотографу то, что он только что убрал.
    if (this.replayingState()) return -1;

    const closed = this.closed();

    return queue.findIndex(hint => !closed.has(hint.record.id));
  });

  /**
   * Карточка нажатого «?»: место у неё — сам значок, а не якорь записи и не вход в контейнер.
   * Нажатие на знак вопроса — просьба этого места, и ответ приходит сюда же, к руке фотографа, даже
   * когда запись рассказывает о другом экране (D14/D16).
   */
  private readonly requested = computed<ActiveHint | null>(() => {
    const id = this.requestedHint();
    if (!id || !this.audience()) return null;

    const record = this.catalog.records.find(item => item.id === id);
    // Разовая карточка живёт на своём экране: якорь двери стоит в шапке всюду, но на чужом экране
    // она очередь не держит — вернулся к себе, и она снова ждёт крестика.
    if (record?.onRequest && record.screen !== this.currentScreen()) return null;
    if (!record?.onRequest && this.requestedOnScreen() !== this.currentScreen()) return null;
    const element = this.siteOf(id, true);

    return record?.coach && element ? { record, element } : null;
  });

  /**
   * The one coach card the photographer may see now — the queue's card at the current place.
   * Просьба идёт впереди очереди: нажал «?» — получил карточку этого места, закрытую в том числе.
   */
  readonly activeHint = computed<ActiveHint | null>(() => this.requested() ?? this.queue()[this.index()] ?? null);

  /** Счётчик «N из M»: номер с единицы, показывать нечего — нули. */
  readonly hintPosition = computed<{ index: number; total: number }>(() => {
    const index = this.index();

    return index < 0 ? { index: 0, total: 0 } : { index: index + 1, total: this.queue().length };
  });

  readonly canNext = computed<boolean>(() => this.index() >= 0 && this.index() < this.queue().length - 1);
  readonly canPrev = computed<boolean>(() => this.index() > 0);

  /**
   * Разделы плейлиста, где остался хоть один урок с роликом. Текст урока и его место в списке
   * задаёт плейлист, а снят ролик или нет — манифест: урок виден ровно когда есть и то, и другое.
   * Плейлист ещё не пришёл или не открылся — разделов нет. Библиотека рисует их разделами списка.
   */
  readonly lessonGroups = computed<PlaylistGroup[]>(() =>
    (this.media.playlist()?.groups ?? [])
      .map(group => ({ ...group, lessons: group.lessons.filter(lesson => this.media.has(lesson.id)) }))
      .filter(group => group.lessons.length > 0),
  );

  /** Everything the library may list: the playlist lessons with a clip, in playlist order. */
  readonly lessons = computed<PlaylistLesson[]>(() => this.lessonGroups().flatMap(group => group.lessons));

  /** Пустую библиотеку открывать нечем: без уроков хост убирает и дверь к ней. */
  readonly hasLessons = computed<boolean>(() => this.lessons().length > 0);

  /**
   * Уроки этого экрана: экран знает о них каталог — у урока плейлиста своего экрана нет. Запись
   * экрана даёт свой ролик или одолженный у другого урока; порядок каталога — порядок флоу, повторов
   * нет. Урок без записи каталога живёт только в библиотеке, запись без урока — только у коуча.
   */
  readonly screenLessons = computed<PlaylistLesson[]>(() => {
    const screen = this.currentScreen();
    if (!screen) return [];

    const byId = new Map(this.lessons().map(lesson => [lesson.id, lesson]));
    const ids = new Set(this.catalog.records.filter(record => record.screen === screen).map(clipLessonId));

    return [...ids].map(id => byId.get(id)).filter((lesson): lesson is PlaylistLesson => lesson !== undefined);
  });

  /** What the library dialog should open on; `seq` makes every call a distinct request. */
  readonly libraryRequest = this.libraryRequestState.asReadonly();
  readonly welcome = this.welcomeState.asReadonly();

  /** `Date.now()` последнего закрытия библиотеки; `null` — в этой вкладке её ещё не закрывали. */
  readonly libraryClosedAt = this.libraryClosedAtState.asReadonly();

  /** Запись, которую попросили нажатием: показу нечего ждать — карточка встаёт сразу. */
  readonly requestedHint = this.requestedState.asReadonly();

  private readonly coachActionState = signal<string | null>(null);
  /** Запись, чью главную кнопку нажали: хост исполняет действие и гасит сигнал `clearCoachAction`. */
  readonly coachAction = this.coachActionState.asReadonly();
  /** Карточка, которую фотограф открыл нажатием «?»: только её нажатие мимо отпускает без записи. */
  readonly pressedHint = computed<string | null>(() => (this.requestedByPress() ? this.requestedState() : null));

  /**
   * Уроки, досмотренные до конца или отмеченные фотографом вручную. Пометка лежит в профиле —
   * значит, идёт за фотографом на любое устройство; в прогоне — своя, до конца вкладки.
   */
  readonly viewedLessons = computed<ReadonlySet<string>>(() =>
    this.replay() ? this.replayViewed() : new Set(this.identity?.profile()?.onboardingProgress?.viewed ?? []),
  );

  /**
   * Уроки, отложенные фотографом сердечком. Отдельная корзина профиля: у просмотра с избранным ни
   * общего правила, ни общего порядка — урок бывает и просмотренным, и избранным сразу. Корзина
   * пуста, пока фотограф сам не нажал сердечко: умолчаний у неё нет (R18).
   */
  readonly favoriteLessons = computed<ReadonlySet<string>>(() => {
    if (this.replay()) return this.replayFavorites() ?? new Set<string>();

    return new Set(this.identity?.profile()?.onboardingProgress?.favorites ?? []);
  });

  /** Оболочка кабинета поднялась: приветствие новичка всплывает только там, в редакторах оно молчит. */
  private readonly cabinet = signal(false);

  /**
   * Сколько в кабинете съёмок, считает хост: список проектов знает он, а движку о проектах знать
   * нечего. `null` — список ещё не прочитан, и вердикта о новичке нет.
   */
  private shootCount: () => number | null = () => null;

  /** Новичок решается один раз за загрузку вкладки. */
  private newcomerSettled = false;

  /** Окно подняли новичку: его первое закрытие выводит карточку к знаку вопроса. */
  private newcomerOpened = false;

  private newcomerTimer: ReturnType<typeof setTimeout> | undefined;

  constructor() {
    // Профиль, плейлист и манифест приезжают врозь: ждём, пока список уроков соберётся целиком.
    effect(() => {
      if (this.newcomerSettled || !this.cabinet() || !this.audience() || !this.hasLessons()) return;

      const shoots = this.shootCount();
      // Список проектов ещё не прочитан: ждём ответа хоста, а не считаем фотографа новичком.
      if (shoots === null) return;

      this.newcomerSettled = true;
      if (shoots >= NEWCOMER_SHOOTS || newcomerShown()) return;

      this.newcomerTimer = setTimeout(() => this.openForNewcomer(), NEWCOMER_DELAY_MS);
    });

    inject(DestroyRef).onDestroy(() => clearTimeout(this.newcomerTimer));
  }

  /**
   * Зовёт только оболочка кабинета — тем и отличается кабинет от редакторов. Счётчик съёмок приезжает
   * тем же входом: считает его хост, читается он сигналами и потому живой.
   */
  noteCabinet(shootCount?: () => number | null): void {
    if (shootCount) this.shootCount = shootCount;
    this.cabinet.set(true);
  }

  /** Оболочка кабинета ушла (редактор): отложенное окно новичка там не всплывает. */
  leaveCabinet(): void {
    this.cabinet.set(false);
  }

  /**
   * Новичку сами поднимаем приветствие, а библиотеку — только по его «Смотреть видео»: первым идёт урок
   * про загрузку съёмки, а пока ролика к нему нет — первый урок с роликом. Ушёл из оболочки кабинета,
   * стоит в мастере создания проекта, успел открыть окно сам, уже смотрел или отложил на сегодня — не
   * поднимаем ничего. Страницы проекта в оболочке окну не мешают.
   */
  private openForNewcomer(): void {
    if (
      !this.cabinet() ||
      this.currentScreen() === WIZARD_SCREEN ||
      !this.audience() ||
      !this.hasLessons() ||
      this.libraryRequestState() !== null ||
      this.closed().has(WELCOME_FLOW) ||
      this.closed().has(welcomeLaterFlow(new Date()))
    ) {
      return;
    }

    markNewcomerShown();
    this.newcomerOpened = true;
    const lessons = this.lessons();

    this.welcomeState.set((lessons.find(lesson => lesson.id === NEWCOMER_LESSON) ?? lessons[0]).id);
  }

  /**
   * Приветствие закрыто. Обложка или «Смотреть видео» — оно пройдено и открывает библиотеку на уроке
   * новичка сразу с роликом; её закрытие потом покажет дверь. Всё прочее — «Позже», клик мимо, Esc —
   * откладывает его до другого дня, и дверь встаёт сразу: где уроки, фотограф узнаёт и так.
   */
  closeWelcome(watch: boolean): void {
    const lessonId = this.welcomeState();
    if (lessonId === null) return;

    this.welcomeState.set(null);
    if (watch) {
      this.complete(WELCOME_FLOW);
      this.openLibrary(lessonId, { play: true });

      return;
    }

    this.dismiss(welcomeLaterFlow(new Date()));
    // Окна, что выжидают паузу после библиотеки, ждут её и после приветствия.
    this.libraryClosedAtState.set(Date.now());
    this.showLibraryDoor();
  }

  /** Экран этого адреса — та же проверка, что и у `currentScreen`, но для любого адреса. */
  private screenOf(url: string): string | null {
    return this.catalog.screens.find(screen => new RegExp(screen.urlPattern).test(url))?.id ?? null;
  }

  /**
   * Экран сменился — листание отпускает своё место: вернувшись сюда снова, фотограф начинает обход
   * заново, с первой незакрытой карточки, а не с той, где остановился в прошлый раз (D10).
   */
  private noteScreen(url: string): string {
    const screen = this.screenOf(url);
    if (screen === this.pagedScreen) return url;

    this.pagedScreen = screen;
    this.paging.set({ screen: null, index: 0 });
    // Повтор живёт ровно на своём экране: ушёл — и экран снова ведёт себя как обычно.
    this.replayingState.set(false);

    return url;
  }

  /**
   * Переход состоялся — карточка «?» покинутого экрана отпускается. На старте рано: guard несохранённых
   * правок может переход отменить, и редактор с его «?» останется на экране.
   */
  private noteArrival(url: string): void {
    const screen = this.screenOf(url);
    if (screen === this.arrivedScreen) return;

    this.arrivedScreen = screen;
    // Отцепленный маршрут (редактор) свои «?» не снимает: без отпуска его карточка всплыла бы на чужом экране.
    const asked = this.requestedState();
    if (asked && !this.catalog.records.find(record => record.id === asked)?.onRequest) this.releaseHint(asked);
  }

  /** Куда смотрит фотограф на этом событии: начатый переход — уже в цель, сорванный — обратно. */
  private eventUrl(e: NavigationStart | NavigationEnd | NavigationCancel | NavigationError): string {
    if (e instanceof NavigationStart) return e.url;

    return e instanceof NavigationEnd ? e.urlAfterRedirects : this.router.url;
  }

  private noteReplay(url: string): string {
    if (isDevMode() && REPLAY_PARAM.test(url)) this.replay.set(true);

    return url;
  }

  /** Очередь экрана в порядке каталога — одна и та же и для самого коуча, и для просьбы по «?». */
  private buildQueue(): ActiveHint[] {
    this.tabsLayout();
    if (!this.audience()) return [];

    const screen = this.currentScreen();
    if (!screen) return [];

    const closed = this.closed();
    const screens = this.screensHere();

    const queue: ActiveHint[] = [];
    for (const record of this.catalog.records) {
      // Запись по просьбе в очередь не встаёт: её поднимает только `requestHint` — разовая карточка
      // без счётчика и стрелок, одним крестиком (R52).
      if (!screens.has(record.screen) || !record.coach || record.onRequest) continue;
      // Держит очередь только та запись, которую сейчас есть где показать: без места её никогда
      // не закрыть, и цепочка встала бы навсегда.
      // Запись более широкого экрана стоит только на своём якоре: «?» и вход в меню узкого экрана
      // принадлежат его туру — иначе чужая карточка займёт голову очереди и раскроет не тот раздел.
      const element = record.screen === screen ? this.siteOf(record.id) : this.anchorOf(record.id);
      if (!element) continue;
      // Порядок флоу: следующий шаг молчит, пока предыдущий не пройден или не скрыт крестиком.
      if (record.after && !closed.has(record.after) && this.siteOf(record.after)) continue;
      queue.push({ record, element });
    }

    return queue;
  }

  private watchTabsLayout(): Signal<boolean> {
    const media: MediaQueryList | null = typeof matchMedia === 'function' ? matchMedia(TABS_LAYOUT_QUERY) : null;
    const wide = signal(media?.matches ?? true);
    if (media) {
      const onChange = (event: MediaQueryListEvent): void => wide.set(event.matches);
      media.addEventListener('change', onChange);
      inject(DestroyRef).onDestroy(() => media.removeEventListener('change', onChange));
    }

    return wide.asReadonly();
  }

  /**
   * Место карточки этой записи в очереди экрана. Значок «?» перевешивает всё: он и есть то, о чём
   * карточка говорит, и носик упирается в него (R46). Дальше вход в контейнер — карточка живёт на
   * кебабе и не переезжает на пункт, когда фотограф открыл меню сам (R44), — и только потом сам
   * якорь. Нажатый значок сюда не заходит: его карточку ведёт `requested`.
   */
  private siteOf(id: string, pressed = false): HTMLElement | undefined {
    const help = this.helpOf(id, pressed);
    const record = this.catalog.records.find(item => item.id === id);
    // The «?» box is painted after the queue reads it — judge its container (a hidden heading row
    // still hides the whole surface) and the clip verdict that paints it: a clip-less «?» stays hidden.
    if (help?.isConnected && hasBox(help.parentElement ?? undefined) && record && this.clipReady(record)) return help;

    return [this.reveals().get(id), this.anchorOf(id)].find(hasBox);
  }

  /**
   * The «?» the card stands on. A press goes to the pressed icon (newest claim); the queue takes the
   * «?» beside the record's anchor — a list repeats «?» per row, and the host picks the anchor row.
   */
  private helpOf(id: string, pressed: boolean): HTMLElement | undefined {
    const helps = this.helps().get(id) ?? [];
    const anchors = this.anchors().get(id) ?? [];
    const beside = pressed ? undefined : helps.find(help => anchors.some(anchor => help.parentElement?.contains(anchor)));

    return beside ?? helps[0];
  }

  /**
   * Якорь записи на экране — новейший из видимых: нажатие на него ведёт его собственный обработчик,
   * а не «мимо карточки».
   */
  anchorOf(id: string): HTMLElement | undefined {
    return this.anchors().get(id)?.find(hasBox);
  }

  registerAnchor(id: string, element: HTMLElement): void {
    const clicks = this.anchorClicks.get(id) ?? new Map<HTMLElement, () => void>();
    this.anchorClicks.set(id, clicks);
    clicks.get(element)?.();
    clicks.delete(element);

    // Только запись через `update`: зовут из effect директивы, чтение карты сделало бы его её читателем.
    this.anchors.update(current => {
      const rest = (current.get(id) ?? []).filter(site => site !== element);

      return new Map(current).set(id, [element, ...rest]);
    });

    const doneOn = this.catalog.records.find(record => record.id === id)?.doneOn;
    if (!doneOn) return;

    // Нажал на само место, о котором говорит карточка, — карточка уходит (D26b). Запись, которая
    // нажатием и проходится, уходит пройденной; всякая другая — как от крестика, и только пока
    // живая карточка стоит именно на ней: чужое нажатие ничего не закрывает.
    const close = (): void => {
      if (doneOn === 'anchor-click') this.complete(id);
      else if (this.activeHint()?.record.id === id) this.dismiss(id);
    };

    element.addEventListener('click', close);
    clicks.set(element, () => element.removeEventListener('click', close));
  }

  /**
   * Снимается только тот же самый элемент: при пересборке хоста новый якорь регистрируется раньше, чем
   * старый уходит, а отцепленный редактор держит свой якорь, пока оболочка ставит и снимает свой.
   */
  unregisterAnchor(id: string, element: HTMLElement): void {
    if (!this.anchors().get(id)?.includes(element)) return;

    this.anchorClicks.get(id)?.get(element)?.();
    this.anchorClicks.get(id)?.delete(element);
    this.anchors.update(current => {
      const rest = (current.get(id) ?? []).filter(site => site !== element);
      const next = new Map(current);
      if (rest.length) next.set(id, rest);
      else next.delete(id);

      return next;
    });
  }

  /**
   * Вход в контейнер, внутри которого висит якорь записи: карточка стоит на нём, пока вход
   * зарегистрирован. Меню движок не раскрывает и на пункт не переезжает — раскрытое фотографом
   * меню карточка не закрывает собой и заново не всплывает (R44).
   */
  registerReveal(id: string, element: HTMLElement): void {
    this.reveals.update(current => new Map(current).set(id, element));
  }

  /**
   * Значок «?» у места: карточка встаёт на него самого. Нажатие ведёт `requestHint` — листалка, а не
   * прохождение: запись `anchor-click` проходится нажатием на своё место, не на знак вопроса (R46).
   *
   * Значков у одной записи бывает несколько — в панели, в шапке окна, в настройках соседнего
   * экрана. Место держит последний заявивший о себе, и нажатый заявляет о себе сам: карточка
   * приходит к той руке, которая её позвала (D14/D16). Очередь берёт «?» у якоря записи (`helpOf`).
   *
   * 🚨 Только запись, никаких чтений `helps()`: зовут отсюда из `effect` директивы, и прочитанный
   * сигнал сделал бы каждый значок читателем карты, которую сам же и пишет. Чужая запись гасила бы
   * его effect, снятие в `onCleanup` убирало бы его из карты, регистрация возвращала — и два знака
   * вопроса ходили бы по кругу, съев поток намертво.
   */
  registerHelp(id: string, element: HTMLElement): HTMLElement | undefined {
    // `update` читает карту без отслеживания: прежнее место отдаём, не делая вызывающего читателем.
    let previous: HTMLElement | undefined;
    this.helps.update(current => {
      const sites = current.get(id) ?? [];
      previous = sites[0];

      return new Map(current).set(id, [element, ...sites.filter(site => site !== element)]);
    });

    return previous;
  }

  /**
   * Как и у якоря: снимается только та же пара id+элемент — пересборка хоста подсказку не гасит.
   * `restore` — место, которое хост занял на время (наведение): живое возвращается, а не пропадает.
   */
  unregisterHelp(id: string, element: HTMLElement, restore?: HTMLElement): void {
    if (!this.helps().get(id)?.includes(element)) return;

    this.helps.update(current => {
      const rest = (current.get(id) ?? []).filter(site => site !== element);
      if (restore?.isConnected && !rest.includes(restore)) rest.unshift(restore);
      const next = new Map(current);
      if (rest.length) next.set(id, rest);
      else next.delete(id);

      return next;
    });
  }

  /** Стоит ли карточка записи на входе в контейнер: якорь внутри меню тогда места не держит (R44). */
  hasReveal(id: string): boolean {
    return !!this.reveals().get(id)?.isConnected;
  }

  /** Как и у якоря: снимается только та же пара id+элемент — пересборка хоста подсказку не гасит. */
  unregisterReveal(id: string, element: HTMLElement): void {
    if (this.reveals().get(id) !== element) return;

    this.reveals.update(current => {
      const next = new Map(current);
      next.delete(id);

      return next;
    });
  }

  /** Дальше по очереди: покинутая карточка скрывается на сервере — второй раз её не покажем. */
  nextHint(): void {
    if (!this.canNext()) return;

    const left = this.activeHint()?.record.id;
    this.movePaging(1);
    // Давно закрытую карточку «дальше» просто минует: скрывать нечего, на сервер ничего не уходит.
    if (left && !this.closed().has(left)) this.dismiss(left);
  }

  /** Назад по очереди: покинутая карточка, как и у «Дальше», скрывается — пролистанное считается увиденным. */
  prevHint(): void {
    if (!this.canPrev()) return;

    const left = this.activeHint()?.record.id;
    this.movePaging(-1);
    if (left && !this.closed().has(left)) this.dismiss(left);
  }

  /**
   * Есть ли у места что показать по нажатию «?»: запись каталога с карточкой коуча, чей шаг флоу уже
   * открыт. Экран записи значок не прячет — «?» стоит там, где его повесил хост, и карточка встаёт
   * на сам значок (D14/D16): знак вопроса — просьба фотографа, а не очередь экрана. Нет записи, нет
   * карточки коуча, ролик не снят, чужая сессия, разовая запись вне своего экрана — хост прячет значок.
   */
  helpAvailable(id: string): boolean {
    if (!this.audience()) return false;

    const record = this.catalog.records.find(item => item.id === id);
    if (!record?.coach) return false;
    // Текст записи карточка не рисует — без снятого ролика по «?» встала бы плита с одним заголовком.
    if (!this.clipReady(record)) return false;
    // Тот же вердикт, что у самой просьбы: разовая карточка на чужом экране не встанет — «?» мёртв.
    if (record.onRequest && record.screen !== this.currentScreen()) return false;

    // Порядок флоу держится на своём экране: следующий шаг молчит, пока предыдущий стоит рядом
    // непройденным. Предыдущего шага на экране нет — держать нечего.
    return !record.after || this.closed().has(record.after) || !this.siteOf(record.after);
  }

  private clipReady(record: OnboardingRecord): boolean {
    return record.video.enabled && this.media.has(clipLessonId(record));
  }

  /**
   * Ролик места — сразу в окне уроков, и он играет: тот же урок, что проверяет `helpAvailable`, и тот
   * же ход, что у обложки карточки коуча, только без карточки. Записи нет — окно не открываем.
   */
  playHelp(id: string): void {
    const record = this.catalog.records.find(item => item.id === id);
    if (record) this.openLibrary(clipLessonId(record), { play: true });
  }

  /**
   * Фотограф сам попросил карточку — нажал «?» у места. Показываем её сейчас же: пройденную,
   * скрытую крестиком, ещё не показанную, записью чужого экрана — любую. Места записи на экране нет
   * совсем — не делаем ничего.
   */
  requestHint(id: string): void {
    // Нажатие на «?», на котором уже стоит карточка — вызванная им или пришедшая очередью тура, — убирает
    // её тем же путём, что и крестик.
    const asked = this.requestedState();
    const shown = asked === id || (asked === null && this.activeHint()?.record.id === id);
    if (shown) return this.dismiss(id);

    this.ask(id, true);
  }

  /**
   * Хост сам поднимает разовую карточку, когда его место на экране (первый одобренный шаблон).
   * Закрытую однажды второй раз не просим; чужую живую просьбу не перебиваем. Без профиля вердикта
   * о закрытом нет — ждём его.
   */
  offerHint(id: string): void {
    if (!this.audience() || this.closed().has(id) || this.requestedState() !== null) return;

    this.ask(id, false);
  }

  /** Главная кнопка карточки: запись пройдена, хост получает действие через `coachAction`. */
  completeAction(id: string): void {
    this.complete(id);
    this.coachActionState.set(id);
  }

  /** Хост исполнил действие — сигнал гаснет, повторный вход на экран его не повторит. */
  clearCoachAction(): void {
    this.coachActionState.set(null);
  }

  private ask(id: string, byPress: boolean): void {
    this.requestedOnScreen.set(this.currentScreen());
    this.requestedByPress.set(byPress);
    this.requestedState.set(id);
  }

  /** Попало ли нажатие в какой-нибудь «?» экрана: его `click` сам подаст просьбу своей карточки. */
  helpContains(target: Node): boolean {
    return [...this.helps().values()].some(sites => sites.some(help => help.contains(target)));
  }

  /**
   * «Подсказки на экране» из меню «?»: обход этого экрана начинается заново, с первой карточки —
   * закрытые включая. Ведёт его то же листание, что и обычный коуч: второй очереди нет. На сервер
   * ничего не уходит — давно закрытая запись остаётся закрытой. Карточек на экране нет — и делать
   * нечего.
   */
  replayScreen(): void {
    if (this.screenHintCount() === 0) return;

    // Снимаем только показанную просьбу: разовая карточка чужого экрана ждёт своего крестика там.
    if (this.requested()) this.requestedState.set(null);
    if (this.silencedScreen() === this.currentScreen()) this.silencedScreen.set(null);
    this.replayingState.set(true);
    this.paging.set({ screen: this.currentScreen(), index: 0 });
  }

  private movePaging(step: number): void {
    const target = this.index() + step;
    // Листание возвращает очередь себе: дальше ведёт рамка экрана, а не просьба. Снимаем только
    // показанную просьбу — разовая карточка чужого экрана ждёт своего крестика там.
    if (this.requested()) this.requestedState.set(null);
    this.paging.set({ screen: this.currentScreen(), index: target });
  }

  complete(id: string): void {
    this.releaseHint(id);
    if (this.replay()) return this.closeInReplay(id);

    this.noteFresh(id);
    this.identity?.completeOnboardingFlow(id);
  }

  dismiss(id: string): void {
    this.releaseHint(id);
    if (this.replay()) return this.closeInReplay(id);

    this.noteFresh(id);
    this.identity?.dismissOnboardingFlow(id);
  }

  /**
   * Крестик на карточке: сама карточка скрывается как обычно, а остальные карточки экрана
   * молчат до перезагрузки вкладки. Нажатие на якорь и листание зовут `dismiss` — экран они не глушат.
   */
  dismissScreen(id: string): void {
    this.dismiss(id);
    this.silencedScreen.set(this.currentScreen());
  }

  /** Сигнал и хранилище двигаются вместе: перезагрузка вкладки продолжает тот же прогон. */
  private noteFresh(id: string): void {
    const current = this.fresh();
    if (!current || current.has(id)) return;

    const next = new Set(current).add(id);
    this.fresh.set(next);
    try {
      localStorage.setItem(FRESH_KEY, JSON.stringify([...next]));
    } catch {
      // Хранилища нет — прогон живёт до перезагрузки вкладки.
    }
  }

  /**
   * Закрыли карточку, на которой стояли, — экран возвращается к своей очереди: просьба исполнена,
   * и листание отпускает место. Без этого указатель листания остался бы на закрытой записи —
   * очередь экрана держит её у себя, — и крестик карточку бы не убрал (D26a). Снаружи зовут, чтобы
   * убрать карточку «?» без записи на сервер: так же, как её отпускает уход с экрана.
   */
  releaseHint(id: string): void {
    const wasActive = this.activeHint()?.record.id === id;
    if (this.requestedState() === id) this.requestedState.set(null);
    if (wasActive) this.paging.set({ screen: null, index: 0 });
  }

  private closeInReplay(id: string): void {
    this.replayClosed.update(set => new Set(set).add(id));
  }

  /** Урок помечается просмотренным, когда ролик досмотрен до конца или фотограф отметил его сам. */
  markViewed(id: string): void {
    if (this.viewedLessons().has(id)) return;
    if (this.replay()) return this.replayViewed.update(set => new Set(set).add(id));

    void this.identity?.markLessonViewed(id);
  }

  /** Фотограф снял отметку сам: урок снова непросмотрен. */
  unmarkViewed(id: string): void {
    if (!this.viewedLessons().has(id)) return;
    if (this.replay()) {
      return this.replayViewed.update(set => new Set([...set].filter(viewed => viewed !== id)));
    }

    void this.identity?.unmarkLessonViewed(id);
  }

  /** Сердечко: одно нажатие кладёт урок в избранное, второе — забирает. Стор пишет оптимистично. */
  toggleFavorite(id: string): void {
    const chosen = this.favoriteLessons().has(id);
    if (this.replay()) {
      return this.replayFavorites.update(set => {
        const next = new Set(set ?? []);
        if (chosen) next.delete(id);
        else next.add(id);

        return next;
      });
    }

    void (chosen ? this.identity?.unmarkLessonFavorite(id) : this.identity?.markLessonFavorite(id));
  }

  /**
   * `play` ставит только тот, кто открывает окно по нажатию на сам ролик (и «Смотреть видео» приветствия):
   * дверь в шапке и меню «?» показывают урок на постере — играть его или нет, решает фотограф (R41).
   */
  openLibrary(lessonId?: string, opts?: { play?: boolean }): void {
    this.libraryRequestState.set({ lessonId: lessonId ?? null, seq: ++this.librarySeq, autoplay: opts?.play ?? false });
  }

  closeLibrary(): void {
    this.libraryRequestState.set(null);
    this.libraryClosedAtState.set(Date.now());
    this.showLibraryDoor();
  }

  private showLibraryDoor(): void {
    if (!this.newcomerOpened) return;

    // Окно, которое фотограф не открывал, закрылось первый раз — показываем, где библиотека живёт.
    // Карточка разовая: закрытую однажды второй раз не просим.
    this.newcomerOpened = false;
    if (!this.closed().has(LIBRARY_DOOR_HINT)) this.ask(LIBRARY_DOOR_HINT, false);
  }
}
